<script setup lang="ts">
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import useDateTitle from '@/composables/useDateTitle';
import * as commonConstants from '@/constants/common';

export interface NoticesCollectionItemProps {
  boldTitle?: string;
  title?: string;
  description?: string;
  timesince?: string;
  timestamp?: string;
  // TODO type cast string to
  // level?: 'info' | 'warning' | 'error';
  level?: string;
  isRead?: boolean;
  id?: number;
  isLoading?: boolean;
}
const props = withDefaults(defineProps<NoticesCollectionItemProps>(), {
  boldTitle: undefined,
  title: 'Notify was updated',
  description: undefined,
  timesince: undefined,
  timestamp: undefined,
  level: 'info',
  isRead: false,
  id: undefined,
  isLoading: false,
});
const emit = defineEmits<{
  'item-click': [id?: number];
  'icon-click': [id?: number];
}>();

function handleItemClick(event: Event): void {
  emit('item-click', props.id);
}

function handleIconClick(event: Event): void {
  event.stopPropagation();
  emit('icon-click', props.id);
}

const dateTitle = useDateTitle(() => props.timestamp);
</script>

<template>
  <a
    :class="[$style.noticesMenuCardCollectionItem, $style[props.level]]"
    data-testid="noticesMenuCardCollectionItem"
    href="javascript:void(0)"
    @click="handleItemClick"
  >
    <template v-if="isLoading">
      <SimpleLoader class="m-auto" />
    </template>
    <template v-else>
      <div :class="$style.leftItem">
        <div :class="$style.titleItem">
          <div>
            <div v-if="!props.isRead" :class="$style.unreadCircle"></div>
          </div>
          <div>
            <span :class="$style.title">
              <span v-if="boldTitle" :class="$style.boldTitle">
                {{ boldTitle }}
              </span>
              {{ title }}
            </span>
          </div>
        </div>
        <div>
          <div :title="props.description">
            <p v-if="props.description" :class="$style.description">
              {{ props.description }}
            </p>
          </div>
        </div>
        <div>
          <div :class="$style.time">
            <KParentTooltip>
              {{ dateTitle }}
            </KParentTooltip>
            <v-icon :class="$style.timeIcon" icon="mdi-clock-outline" />
            <template v-if="props.timesince">
              {{ props.timesince }} ago
            </template>
            <template v-else>{{ commonConstants.EM_DASH }}</template>
          </div>
        </div>
      </div>
      <div :class="$style.rightItem">
        <v-btn
          density="comfortable"
          :ripple="false"
          :class="$style.readIcon"
          variant="plain"
          icon
          @click="handleIconClick"
        >
          <KParentTooltip location="start" :offset="0">
            {{ props.isRead ? 'Mark as unread' : 'Mark as read' }}
          </KParentTooltip>
          <v-icon
            :icon="
              props.isRead ? 'mdi-email-open-outline' : 'mdi-email-outline'
            "
          />
        </v-btn>
      </div>
    </template>
  </a>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.noticesMenuCardCollectionItem {
  display: flex;
  justify-content: space-between;
  height: 120px;
  padding: 10px;
  text-decoration: none;
  color: colors.$color-text-primary;

  &:hover .title {
    text-decoration: underline;
  }
}
.noticesMenuCardCollectionItem.info {
}
.noticesMenuCardCollectionItem.warning {
  background-color: colors.$color-orange-50;
}
.noticesMenuCardCollectionItem.error {
  background-color: colors.$color-red-50;
}
  .leftItem {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-left: 15px;
  }
    .titleItem {
      display: flex;
      align-items: center;
      margin-left: -15px;
    }
    .titleItem > div:first-child {
      width: 10px;
      margin-right: 5px;
    }
      .unreadCircle {
        background-color: colors.$color-brand-800;
        width: 10px;
        height: 10px;
        border-radius: 50%;
      }
      .title {
        font-weight: 400;
        color: colors.$color-text-primary;
        text-decoration: none;
      }
        .boldTitle {
          font-weight: 500;
        }
    .description {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      font-size: 0.875rem;
      line-height: calc(20 / 14);
      height: 40px;
      margin: 0;
    }
      .time {
        color: colors.$color-text-secondary;
        font-size: 0.875rem;
        line-height: calc(20 / 14);
        display: inline-flex;
        align-items: center;
      }
        .timeIcon {
          font-size: 15px;
          margin-right: 4px;
        }
  .rightItem {
    margin-left: 100px;
  }
    .readIcon {
      margin-top: -5px;
      margin-right: 5px;
      opacity: 0;
      color: colors.$color-brand-200;

      .noticesMenuCardCollectionItem:focus-within &,
      .noticesMenuCardCollectionItem:hover & {
        opacity: 1;
      }

      &:focus,
      &:hover {
        opacity: 1;
      }

      &:hover {
        color: colors.$color-brand-400;
      }
      &:active {
        color: colors.$color-brand-300;
      }
    }
</style>
